// src/common/ui/modal-dialogs.js
// Modal dialog utilities

import { getCommonBasePath, getRelativePath } from '../utils/path-utils.js';

let filePickerModal = null;

/**
 * Ensure the file picker modal exists
 * @returns {HTMLElement} - Modal element
 */
function ensureFilePickerModal() {
  if (filePickerModal) return filePickerModal;

  const modal = document.createElement('div');
  modal.id = 'fc-file-picker-modal';
  Object.assign(modal.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.35)',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '10000',
  });

  const box = document.createElement('div');
  Object.assign(box.style, {
    width: 'min(520px, 92vw)',
    background: '#fff',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    fontFamily: 'Segoe UI, system-ui, sans-serif',
  });

  const title = document.createElement('h4');
  title.textContent = 'Choose the correct file';
  Object.assign(title.style, { margin: '0 0 12px', fontSize: '16px' });

  const search = document.createElement('input');
  search.type = 'search';
  search.placeholder = 'Select a file…';
  search.setAttribute('aria-label', 'Select or filter files');
  Object.assign(search.style, {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '12px',
  });

  const select = document.createElement('select');
  Object.assign(select.style, {
    display: 'none',
  });

  const results = document.createElement('div');
  results.setAttribute('role', 'listbox');
  results.setAttribute('aria-label', 'Matching files');
  Object.assign(results.style, {
    display: 'none',
    position: 'absolute',
    left: '16px',
    right: '16px',
    maxHeight: '55vh',
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.16)',
    zIndex: '1',
  });
  
  // Add CSS for optgroup styling
  const style = document.createElement('style');
  style.textContent = `
    #fc-file-picker-modal select optgroup {
      font-weight: 600;
      color: #374151;
      font-style: normal;
    }
    #fc-file-picker-modal select option {
      padding: 4px 8px;
      font-weight: 400;
    }
  `;
  document.head.appendChild(style);

  const details = document.createElement('div');
  details.className = 'file-picker-details';
  Object.assign(details.style, {
    fontSize: '12px',
    color: '#4b5563',
    wordBreak: 'break-all',
    marginBottom: '12px',
  });

  const actions = document.createElement('div');
  Object.assign(actions.style, { display: 'flex', gap: '8px', justifyContent: 'flex-end' });

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';
  Object.assign(cancel.style, {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    background: '#f9fafb',
    cursor: 'pointer',
  });

  const ok = document.createElement('button');
  ok.type = 'button';
  ok.textContent = 'Use file';
  Object.assign(ok.style, {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #b91c1c',
    background: '#b91c1c',
    color: '#fff',
    cursor: 'pointer',
  });

  actions.append(cancel, ok);
  box.style.position = 'relative';
  box.append(title, search, results, select, details, actions);
  modal.append(box);
  document.body.append(modal);

  modal._select = select;
  modal._search = search;
  modal._results = results;
  modal._details = details;
  modal._ok = ok;
  modal._cancel = cancel;
  modal._files = new Map();
  modal._basePath = '';
  modal._allFiles = [];
  modal._currentFileId = '';

  const getDisplayName = (file, fallbackId, basePath = '') => {
    if (!file) return `File ${fallbackId || ''}`.trim();
    
    // Try to show relative path from base for folder hierarchy
    if (file.path && basePath) {
      const relative = getRelativePath(file.path, basePath);
      if (relative) return relative;
    }
    
    // Fallback to just filename
    if (file.path) {
      const name = file.path.split(/[\\/]/).pop();
      if (name) return name;
    }
    if (file.displayLabel) return file.displayLabel;
    if (file.title) return file.title;
    return file.name || `File ${fallbackId || ''}`.trim();
  };

  modal._updateDetails = () => {
    const selectedId = modal._select.value;
    const info = modal._files.get(selectedId);
    if (!info) {
      modal._details.textContent = 'Select a file to view its details.';
      return;
    }

    // Show DB-root-relative path when possible for consistency with the rest of UI.
    const fullPath = info?.path || '';
    const relativePath = fullPath ? getRelativePath(fullPath, modal._basePath || '') : '';
    const pages = info?.page_count != null ? `${info.page_count} page${info.page_count === 1 ? '' : 's'}` : null;
    const pieces = [];
    if (relativePath || fullPath) pieces.push(relativePath || fullPath);
    if (pages) pieces.push(pages);
    modal._details.textContent = pieces.join(' • ');  
  };

  modal._populate = (files, currentId) => {
    if (currentId !== undefined) {
      modal._allFiles = files || [];
      modal._currentFileId = currentId != null ? String(currentId) : '';
    }
    const selectedId = modal._select.value || (currentId !== undefined ? modal._currentFileId : '');
    const query = modal._search.value.trim().toLocaleLowerCase();
    modal._files.clear();
    modal._results.innerHTML = '';
    modal._select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select a file…';
    modal._select.append(placeholder);

    // Prefer DB source root when available; fallback to computed common prefix.
    const explicitBasePath = (
      modal._allFiles.find(file => typeof file?._basePath === 'string' && file._basePath.length > 0)?._basePath
    ) || '';
    const basePath = explicitBasePath || getCommonBasePath(modal._allFiles);
    modal._basePath = basePath;
    console.log('[FilePicker] Common base path:', basePath);
    console.log('[FilePicker] Total files:', modal._allFiles.length);

    // Group files by their folder relative to base path (database root)
    const filesByFolder = new Map();
    
    modal._allFiles.forEach(file => {
      if (file?.file_id === undefined || file?.file_id === null) return;
      const id = String(file.file_id);
      modal._files.set(id, file);
      
      // Get relative path from database base and extract folder structure
      const relativePath = getRelativePath(file.path, basePath);
      const displayName = getDisplayName(file, id, basePath);
      if (query && !displayName.toLocaleLowerCase().includes(query)) return;
      console.log('[FilePicker] File:', file.path, '→ Relative:', relativePath);
      
      const parts = relativePath.split('/');
      const filename = parts[parts.length - 1];
      // Only use the folder portion relative to the database base
      const folder = parts.length > 1 ? parts.slice(0, -1).join('/') : '';
      
      if (!filesByFolder.has(folder)) {
        filesByFolder.set(folder, []);
      }
      filesByFolder.get(folder).push({ id, filename, file });
    });
    
    console.log('[FilePicker] Folders found:', Array.from(filesByFolder.keys()));

    // Sort folders - root files first, then alphabetically
    const sortedFolders = Array.from(filesByFolder.keys()).sort((a, b) => {
      if (a === '' && b !== '') return -1;
      if (a !== '' && b === '') return 1;
      return a.localeCompare(b);
    });

    // Create options grouped by folder
    const appendResult = (id, label) => {
      const result = document.createElement('button');
      result.type = 'button';
      result.setAttribute('role', 'option');
      result.textContent = label;
      Object.assign(result.style, {
        display: 'block',
        width: '100%',
        padding: '9px 12px',
        border: '0',
        background: '#fff',
        color: '#111827',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
      });
      result.addEventListener('mouseenter', () => { result.style.background = '#f3f4f6'; });
      result.addEventListener('mouseleave', () => { result.style.background = '#fff'; });
      result.addEventListener('mousedown', (event) => event.preventDefault());
      result.addEventListener('click', () => {
        modal._select.value = id;
        modal._search.value = getDisplayName(modal._files.get(id), id, basePath);
        modal._updateDetails();
        modal._results.style.display = 'none';
      });
      modal._results.append(result);
    };

    sortedFolders.forEach(folder => {
      const filesInFolder = filesByFolder.get(folder);
      
      // Sort files alphabetically within each folder
      filesInFolder.sort((a, b) => a.filename.localeCompare(b.filename));
      
      if (folder === '') {
        // Root files - no optgroup
        filesInFolder.forEach(({ id, filename }) => {
          const opt = document.createElement('option');
          opt.value = id;
          opt.textContent = filename;
          modal._select.append(opt);
          appendResult(id, filename);
        });
      } else {
        // Files in folder - use optgroup
        const optgroup = document.createElement('optgroup');
        const heading = document.createElement('div');
        heading.textContent = `Folder: ${folder}`;
        Object.assign(heading.style, {
          padding: '8px 12px 4px',
          color: '#374151',
          fontSize: '12px',
          fontWeight: '600',
        });
        modal._results.append(heading);
        optgroup.label = `📁 ${folder}`;
        
        filesInFolder.forEach(({ id, filename }) => {
          const opt = document.createElement('option');
          opt.value = id;
          opt.textContent = `  ${filename}`; // Indent with spaces
          optgroup.append(opt);
          appendResult(id, filename);
        });
        
        modal._select.append(optgroup);
      }
    });

    if (filesByFolder.size === 0 && query) {
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = 'No matching files';
      empty.disabled = true;
      modal._select.append(empty);

      const emptyResult = document.createElement('div');
      emptyResult.textContent = 'No matching files';
      Object.assign(emptyResult.style, { padding: '12px', color: '#6b7280' });
      modal._results.append(emptyResult);
    }

    modal._select.value = modal._files.has(selectedId) ? selectedId : '';
    if (currentId !== undefined) {
      const selectedFile = modal._files.get(modal._select.value);
      modal._search.value = selectedFile ? getDisplayName(selectedFile, modal._select.value, basePath) : '';
    }
    modal._updateDetails();
  };

  modal._showResults = () => {
    modal._results.style.top = `${search.offsetTop + search.offsetHeight + 4}px`;
    modal._results.style.display = modal._results.childElementCount ? 'block' : 'none';
  };

  modal._hideResults = () => {
    modal._results.style.display = 'none';
  };

  modal._show = () => {
    modal.style.display = 'flex';
    modal._search.focus({ preventScroll: true });
    modal._showResults();
  };

  modal._hide = () => {
    modal._hideResults();
    modal.style.display = 'none';
  };

  modal._select.addEventListener('change', () => modal._updateDetails());
  modal._search.addEventListener('input', () => {
    modal._select.value = '';
    modal._populate(modal._allFiles, undefined);
    modal._showResults();
  });
  modal._search.addEventListener('focus', () => modal._showResults());
  modal.addEventListener('mousedown', (event) => {
    if (event.target !== modal._search && !modal._results.contains(event.target)) {
      modal._hideResults();
    }
  });

  filePickerModal = modal;
  return modal;
}

/**
 * Show file picker dialog
 * @param {Array} files - Array of file objects
 * @param {string|number} currentFileId - Currently selected file ID
 * @returns {Promise<string|null>} - Selected file ID or null if cancelled
 */
export function showFilePickerDialog(files, currentFileId) {
  return new Promise((resolve) => {
    const modal = ensureFilePickerModal();

    const finish = (value) => {
      modal._hide();
      modal._ok.onclick = null;
      modal._cancel.onclick = null;
      window.removeEventListener('keydown', onKey);
      resolve(value);
    };

    const onKey = (event) => {
      if (event.key === 'Enter') {
        finish(modal._select.value || null);
      } else if (event.key === 'Escape') {
        finish(null);
      }
    };

    modal._search.value = '';
    modal._populate(files || [], currentFileId);
    modal._ok.onclick = () => finish(modal._select.value || null);
    modal._cancel.onclick = () => finish(null);
    window.addEventListener('keydown', onKey);
    modal._show();
  });
}
